import { useState } from 'react';
import { Button } from '../../design-system';
import EntityChip from './EntityChip';
import ConfirmCommand from './ConfirmCommand';
import VoiceButton from '../VoiceButton';
import { parseCommand } from '../../lib/command';
import './CommandBar.css';

// The command bar: two modes over one confirmation path.
//
// ⚠️ THE TEXT BOX IS THE DEFAULT, BY PRODUCT DECISION. The original build
// opened on Fields, on the reasoning that chips have no parsing step and so
// cannot be misread. That argument still holds and is why Fields remains one
// click away — it is the reliable path when a command will not parse, and the
// confirmation screen points at it by name when a slot comes back unresolved.
//
// It was overruled for a good reason: typing the whole instruction is what
// people actually reach for, and the parser now segments through the model
// before anything resolves, so an unfamiliar phrasing is recoverable rather
// than final. Opening on the slower, safer control taught nobody the fast one
// existed.
//
// ⚠️ BOTH MODES END AT THE SAME CONFIRMATION SCREEN. That is the design rule
// from the spec and it is what makes the two safe to mix: whatever produced the
// intent, a human reads the same summary and presses the same button, and the
// server re-validates the same way.
export default function CommandBar({ onDone }) {
  const [mode, setMode] = useState('text');
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState('');

  // Chip mode state.
  const [service, setService] = useState(null);
  const [client, setClient] = useState(null);
  const [preparer, setPreparer] = useState(null);
  const [reviewer, setReviewer] = useState(null);

  const runParse = async () => {
    const t = text.trim();
    if (!t) return;
    setParsing(true);
    setError('');
    try {
      setParsed(await parseCommand(t));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read that command');
    } finally {
      setParsing(false);
    }
  };

  // Chip mode builds the same shape the parser produces, so ConfirmCommand and
  // the execute endpoint need no idea which mode it came from.
  const runChips = async () => {
    if (!client) { setError('Pick a client'); return; }
    const parts = [
      service?.label, client.label,
      preparer ? `prep ${preparer.label}` : '',
      reviewer ? `rev ${reviewer.label}` : '',
    ].filter(Boolean).join(' ');
    setParsing(true);
    setError('');
    try {
      // ⚠️ STILL GOES THROUGH THE SERVER'S PARSER rather than posting the ids
      // directly. It costs one round trip and buys a single execution path:
      // one place that validates, one place that logs, one confirmation shape.
      // A second "chips-only" endpoint would be the duplication this codebase
      // keeps paying for.
      setParsed(await parseCommand(parts));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not build that command');
    } finally {
      setParsing(false);
    }
  };

  const reset = () => {
    setParsed(null); setText(''); setError('');
    setService(null); setClient(null); setPreparer(null); setReviewer(null);
  };

  if (parsed) {
    return (
      <ConfirmCommand
        parsed={parsed}
        onCancel={() => setParsed(null)}
        onDone={(result) => { reset(); onDone?.(result); }}
      />
    );
  }

  return (
    <div className="cmd-bar">
      <div className="cmd-modes" role="tablist">
        <button
          type="button" role="tab" aria-selected={mode === 'chips'}
          className={`cmd-mode${mode === 'chips' ? ' is-on' : ''}`}
          onClick={() => setMode('chips')}
        >
          Fields
        </button>
        <button
          type="button" role="tab" aria-selected={mode === 'text'}
          className={`cmd-mode${mode === 'text' ? ' is-on' : ''}`}
          onClick={() => setMode('text')}
        >
          Type a command
        </button>
      </div>

      {mode === 'chips' ? (
        <div className="cmd-chips">
          <EntityChip label="Service" type="service" value={service} onChange={setService} autoFocus />
          <EntityChip label="Client" type="client" value={client} onChange={setClient} />
          <EntityChip label="Preparer" type="employee" value={preparer} onChange={setPreparer} />
          <EntityChip label="Reviewer" type="employee" value={reviewer} onChange={setReviewer} />
          <Button variant="primary" loading={parsing} onClick={runChips}>Review</Button>
        </div>
      ) : (
        <div className="cmd-text">
          <input
            className="cmd-text-input"
            value={text}
            placeholder="gst3b sharma traders prep ankit rev priya"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runParse(); }}
          />
          {/* ⚠️ IT FILLS THE BOX. NOT Review, NOT Confirm. Dictation puts the
              words where a person would have typed them and stops, because
              speech adds a hearing mistake on top of the model's reading one
              and the review screen is the only place either gets caught. */}
          <VoiceButton
            label="Hold to speak"
            disabled={parsing}
            onTranscript={(spoken) => setText((prev) => (prev.trim() ? `${prev.trim()} ${spoken}` : spoken))}
            onError={(message) => setError(message)}
          />
          <Button variant="primary" loading={parsing} onClick={runParse}>Review</Button>
        </div>
      )}

      {error && <p className="cmd-error">{error}</p>}
      {mode === 'text' && (
        <p className="cmd-hint">
          Nothing is created until you confirm on the next screen.
        </p>
      )}
    </div>
  );
}
